<script setup>
//SelectedPublic.vue
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const { t } = useI18n();

// Props
const props = defineProps({
  tableOptions: {
    type: Array,
    required: true,
    default: () => [],
  },
  phoneColumnOptions: {
    type: Array,
    required: true,
    default: () => [],
  },
  nameColumnOptions: {
    type: Array,
    required: true,
    default: () => [],
  },
  isLoading: Boolean,
  initialSelectedTable: {
    type: String,
    default: '',
  },
  initialSelectedPhoneColumn: {
    type: String,
    default: '',
  },
  initialSelectedContactColumn: {
    type: String,
    default: '',
  },
});

// Emits
const emit = defineEmits(['submit', 'table-changed', 'preview-table', 'validation-changed']);

// Reactive state
const selectedTable = ref('');
const selectedPhoneColumn = ref('');
const selectedContactColumn = ref('');
const isLoadingPhoneColumns = ref(false);
const isLoadingNameColumns = ref(false);

// Form validation rules - contato agora é sempre obrigatório
const rules = computed(() => ({
  selectedTable: { required },
  selectedPhoneColumn: { required },
  selectedContactColumn: { required },
}));

const v$ = useVuelidate(rules, { selectedTable, selectedPhoneColumn, selectedContactColumn });

// Form errors
const formErrors = computed(() => ({
  selectedTable: v$.value.selectedTable.$error
    ? t('WHATSAPP_TEMPLATES.PUBLIC.ERROR_NOT_SELECTED_TABLE')
    : '',
  selectedPhoneColumn: v$.value.selectedPhoneColumn.$error
    ? t('WHATSAPP_TEMPLATES.PUBLIC.ERROR_NOT_SELECTED_PHONE_COLUMN')
    : '',
  selectedContactColumn: v$.value.selectedContactColumn.$error
    ? t('WHATSAPP_TEMPLATES.PUBLIC.ERROR_NOT_SELECTED_CONTACT_COLUMN')
    : '',
}));

// Main form validation
const isFormValid = computed(() => {
  if (!selectedTable.value) return false;
  
  // Se ainda está carregando, considerar como não válido temporariamente
  if (isLoadingPhoneColumns.value || isLoadingNameColumns.value) return false;
  
  // Se a tabela foi selecionada mas ainda não temos opções e não está carregando,
  // aguardar um pouco mais antes de considerar erro
  if (selectedTable.value && !isLoadingPhoneColumns.value && props.phoneColumnOptions.length === 0) {
    // Adicionar um delay para evitar erro prematuro
    setTimeout(() => {
      if (props.phoneColumnOptions.length === 0) {
        console.warn('Nenhuma coluna de telefone encontrada após carregamento');
      }
    }, 1000);
    return false;
  }
  
  if (selectedTable.value && !isLoadingNameColumns.value && props.nameColumnOptions.length === 0) {
    setTimeout(() => {
      if (props.nameColumnOptions.length === 0) {
        console.warn('Nenhuma coluna de nome encontrada após carregamento');
      }
    }, 1000);
    return false;
  }
  
  return selectedTable.value && selectedPhoneColumn.value && selectedContactColumn.value;
});

// Event handlers
const onTableChange = (newTable) => {
  selectedTable.value = newTable;
  v$.value.selectedTable.$touch();

  // Reset columns
  selectedPhoneColumn.value = '';
  selectedContactColumn.value = '';
  v$.value.selectedPhoneColumn.$reset();
  v$.value.selectedContactColumn.$reset();

  if (newTable) {
    isLoadingPhoneColumns.value = true;
    isLoadingNameColumns.value = true;
  } else {
    isLoadingPhoneColumns.value = false;
    isLoadingNameColumns.value = false;
  }

  emit('table-changed', newTable);
};

const onPhoneColumnChange = (newColumn) => {
  selectedPhoneColumn.value = newColumn;
  v$.value.selectedPhoneColumn.$touch();
};

const onContactColumnChange = (newColumn) => {
  selectedContactColumn.value = newColumn;
  v$.value.selectedContactColumn.$touch();
};

const validateAndEmit = async () => {
  if (selectedTable.value && !isLoadingPhoneColumns.value && props.phoneColumnOptions.length === 0) {
    return false;
  }

  if (selectedTable.value && !isLoadingNameColumns.value && props.nameColumnOptions.length === 0) {
    return false;
  }

  if (!isFormValid.value) {
    return false;
  }

  emit('submit', {
    selectedTable: selectedTable.value,
    selectedPhoneColumn: selectedPhoneColumn.value,
    addContact: true, // Sempre true
    selectedContactColumn: selectedContactColumn.value,
  });
  return true;
};

// Watchers
watch(() => props.phoneColumnOptions, () => {
  if (selectedTable.value && isLoadingPhoneColumns.value) {
    isLoadingPhoneColumns.value = false;
  }
}, { immediate: true, deep: true });

watch(() => props.nameColumnOptions, () => {
  if (selectedTable.value && isLoadingNameColumns.value) {
    isLoadingNameColumns.value = false;
  }
}, { immediate: true, deep: true });

watch(isFormValid, (newValue) => {
  emit('validation-changed', newValue);
}, { immediate: false });

watch([() => props.initialSelectedTable, () => props.phoneColumnOptions, () => props.nameColumnOptions], 
  ([newTable, phoneOptions, nameOptions]) => {
    // Se a tabela foi definida e temos as opções de colunas
    if (newTable && phoneOptions.length > 0 && nameOptions.length > 0) {
      // Auto-selecionar as colunas salvas se disponíveis
      if (props.initialSelectedPhoneColumn && phoneOptions.some(opt => opt.value === props.initialSelectedPhoneColumn)) {
        selectedPhoneColumn.value = props.initialSelectedPhoneColumn;
      }
      if (props.initialSelectedContactColumn && nameOptions.some(opt => opt.value === props.initialSelectedContactColumn)) {
        selectedContactColumn.value = props.initialSelectedContactColumn;
      }
    }
  }, 
  { immediate: true }
);

// Sync with initial props
watch(() => props.initialSelectedTable, (newValue) => {
  if (newValue !== selectedTable.value) {
    selectedTable.value = newValue;
  }
}, { immediate: true });

watch(() => props.initialSelectedPhoneColumn, (newValue) => {
  if (newValue !== selectedPhoneColumn.value) {
    selectedPhoneColumn.value = newValue;
  }
}, { immediate: true });

watch(() => props.initialSelectedContactColumn, (newValue) => {
  if (newValue !== selectedContactColumn.value) {
    selectedContactColumn.value = newValue;
  }
}, { immediate: true });

// Expose methods and reactive properties
defineExpose({
  validateAndEmit,
  selectedTable,
  selectedPhoneColumn,
  selectedContactColumn
});
</script>

<template>
  <div class="space-y-6">
    <!-- Tabela e Coluna de Telefone na mesma linha -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Tabela -->
      <div class="flex flex-col gap-2">
        <label for="table" class="text-sm font-medium text-n-slate-12 dark:text-white flex items-center gap-2">
          <span class="text-green-600 dark:text-green-400">📋</span>
          {{ t('WHATSAPP_TEMPLATES.PUBLIC.TABLE_NAME') }}
          <span class="text-red-500">*</span>
        </label>
        <ComboBox
          id="table"
          v-model="selectedTable"
          :options="tableOptions"
          :placeholder="t('WHATSAPP_TEMPLATES.PUBLIC.TABLE_PLACEHOLDER')"
          :message="formErrors.selectedTable"
          :has-error="!!formErrors.selectedTable"
          @update:modelValue="onTableChange"
        />
      </div>

      <!-- Coluna de Telefone -->
      <div class="flex flex-col gap-2">
        <label for="phone-column" class="text-sm font-medium text-n-slate-12 dark:text-white flex items-center gap-2">
          <span class="text-green-600 dark:text-green-400">📱</span>
          {{ t('WHATSAPP_TEMPLATES.PUBLIC.PHONE_COLUMN_LABEL') }}
          <span class="text-red-500">*</span>
          <span v-if="isLoadingPhoneColumns" class="ml-2">
            <svg class="animate-spin h-4 w-4 text-woot-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
        </label>
        <ComboBox
          id="phone-column"
          v-model="selectedPhoneColumn"
          :options="phoneColumnOptions"
          :placeholder="!selectedTable ? 'Selecione uma tabela primeiro' : isLoadingPhoneColumns ? 'Carregando...' : phoneColumnOptions.length === 0 ? 'Nenhuma coluna disponível' : t('WHATSAPP_TEMPLATES.PUBLIC.PHONE_COLUMN_PLACEHOLDER')"
          :disabled="!selectedTable || isLoadingPhoneColumns || phoneColumnOptions.length === 0"
          :message="formErrors.selectedPhoneColumn || (!selectedTable ? '' : isLoadingPhoneColumns ? 'Carregando colunas de telefone...' : (selectedTable && !isLoadingPhoneColumns && phoneColumnOptions.length === 0 ? 'Nenhuma coluna para telefone encontrada' : ''))"
          :has-error="!!formErrors.selectedPhoneColumn || (selectedTable && !isLoadingPhoneColumns && phoneColumnOptions.length === 0)"
          @update:modelValue="onPhoneColumnChange"
        />
      </div>
    </div>

    <!-- Coluna de Contato com explicação -->
    <div class="flex flex-col gap-2">
      <label for="contact-column" class="text-sm font-medium text-n-slate-12 dark:text-white flex items-center gap-2">
        <span class="text-green-600 dark:text-green-400">👤</span>
        {{ t('WHATSAPP_TEMPLATES.PUBLIC.CONTACT_COLUMN_LABEL') }}
        <span class="text-red-500">*</span>
        
        <span v-if="isLoadingNameColumns" class="ml-2">
          <svg class="animate-spin h-4 w-4 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </span>
      </label>
      
      <!-- Texto explicativo direto -->
      <div class="flex items-start gap-2 mb-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-woot-400  rounded-lg">
        <div class="text-blue-600 dark:text-blue-400 text-sm mt-0.5">💡</div>
        <div class="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
          <span class="font-medium">Campo obrigatório:</span> Os nomes desta coluna serão usados para criar automaticamente os contatos no sistema durante o envio.
        </div>
      </div>
      
      <ComboBox
        id="contact-column"
        v-model="selectedContactColumn"
        :options="nameColumnOptions"
        :placeholder="!selectedTable ? 'Selecione uma tabela primeiro' : isLoadingNameColumns ? 'Carregando...' : nameColumnOptions.length === 0 ? 'Nenhuma coluna disponível' : t('WHATSAPP_TEMPLATES.PUBLIC.CONTACT_COLUMN_PLACEHOLDER')"
        :disabled="!selectedTable || isLoadingNameColumns || nameColumnOptions.length === 0"
        :message="formErrors.selectedContactColumn || (!selectedTable ? '' : isLoadingNameColumns ? 'Carregando colunas de nome...' : (selectedTable && !isLoadingNameColumns && nameColumnOptions.length === 0 ? 'Nenhuma coluna para nome encontrada' : ''))"
        :has-error="!!formErrors.selectedContactColumn || (selectedTable && !isLoadingNameColumns && nameColumnOptions.length === 0)"
        @update:modelValue="onContactColumnChange"
      />
    </div>
  </div>
</template>

<style scoped>
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>